import React, { useCallback } from 'react';
import Link from 'gatsby-link';
import withLocation from 'src/components/withLocation';
import classnames from 'classnames';
import { WindowLocation } from '@reach/router';
import { Accordion, Button } from 'semantic-ui-react';

import { ENTITLED_NAV, UNENTITLED_NAV, NavItem, SubnavItem } from '../nav-constants';
import { Icon } from 'src/components/shared';
import LinkWithPrevPath from 'src/components/LinkWithPrevPath';

import * as Styles from 'src/styles/header.module.scss';
import useNavItems from 'src/hooks/useNavItems';

interface MenuProps {
    toggleMenu: () => void;
    loggedIn: boolean;
    logout: () => void;
    location: WindowLocation;
    entitled: boolean;
    shopifyUrl: string;
}

const AccordionMenu = ({
    toggleMenu,
    entitled,
    loggedIn,
    shopifyUrl,
    logout,
    location: { pathname },
}: MenuProps) => {
    /* eslint-disable react-hooks/exhaustive-deps */
    const handleLogout = useCallback(() => {
        logout();
        toggleMenu();
    }, []);

    const PanelTitle = ({ label, url, subnav }: NavItem) => {
        const isChildActive = subnav?.some(
            (item) => pathname !== '/' && item.pathname?.includes(pathname)
        );

        const panelTitleClassnames = classnames('upper', Styles.panelTitle, {
            noChevron: url,
            [Styles.current]: isChildActive,
        });

        return url ? (
            <a href={url} className={panelTitleClassnames}>
                {label}
            </a>
        ) : (
            <h2 className={panelTitleClassnames}>{label}</h2>
        );
    };

    const subNavItems = (item: SubnavItem) => {
        const to = item.pathname || item.url;

        return (
            <li className={item.classname && `accordion-${item.classname}`}>
                <Link to={to} activeClassName={Styles.current}>
                    {item.label}
                </Link>
            </li>
        );
    };

    const navItems = useNavItems();

    const headerItems = navItems.map((item, index) => ({
        key: `panel-${index}`,
        title: {
            content: PanelTitle(item),
            icon: '',
        },
        content: {
            content: (
                <ul className={item.classname && `accordion-${item.classname}`}>
                    {item.subnav?.map(subNavItems)}
                </ul>
            ),
        },
    }));

    return (
        <div style={{ height: window.innerHeight }} className={Styles.headerAccordion}>
            <button onClick={toggleMenu} className={Styles.close}>
                <Icon name="pv-close" size={15} />
            </button>

            <Accordion panels={headerItems} exclusive={false} fluid />

            {loggedIn ? (
                <ul className={Styles.userMenu}>
                    <li>
                        <Link to="/account">Account</Link>
                    </li>
                    {entitled && (
                        <li>
                            <Link to="/account/settings">Settings</Link>
                        </li>
                    )}
                    {entitled && (
                        <li>
                            <Link to="/account/subscription">Subscription</Link>
                        </li>
                    )}
                    <li>
                        <Link to="/account/communications">Communications</Link>
                    </li>
                    <li>
                        <button onClick={handleLogout}>Log out</button>
                    </li>
                </ul>
            ) : (
                <div className={Styles.signInButtons}>
                    <Button as={LinkWithPrevPath} to="/continue" fluid>
                        Log In
                    </Button>
                    <Button as={LinkWithPrevPath} to="/getting-started" fluid>
                        Get started
                    </Button>
                </div>
            )}
        </div>
    );
};

export default withLocation(AccordionMenu);
